import type { LiveBillAgent, LiveBillResponse } from '@monorepo/shared'
import type { CustomerOverviewMetricsResponse } from '@monorepo/shared/schemas/customers'
import type { CustomerAgentUsageResponse } from '@monorepo/shared/schemas/usage'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Bot, ChevronDown, RefreshCw, Zap } from 'lucide-react'
import { useMemo, useState } from 'react'
import {
  type BillingCycle,
  BillingCycleSelector,
} from '@/features/billing/components/BillingCycleSelector'
import { RevenueChart } from '@/shared/components/charts/RevenueChart'
import { Button } from '@/shared/components/ui/button'
import { Calendar as CalendarComponent } from '@/shared/components/ui/calendar'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import { Progress } from '@/shared/components/ui/progress'
import { customerDetailService } from '../../services/customerDetailService'
import { type Customer, customerService } from '../../services/customerService'
import { getPeriodDates, getTimeRangeLabel } from '../../utils/periodDates'

interface OverviewTabProps {
  customer: Customer
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

function CardSkeleton() {
  return (
    <Card className='bg-transparent border h-24 flex items-center'>
      <CardContent className='px-3 py-0 w-full'>
        <div className='animate-pulse'>
          <div className='h-4 bg-slate-300 dark:bg-slate-600 rounded w-20 mb-1' />
          <div className='h-6 bg-slate-300 dark:bg-slate-600 rounded w-16' />
        </div>
      </CardContent>
    </Card>
  )
}

export function OverviewTab({ customer }: OverviewTabProps) {
  const queryClient = useQueryClient()
  const [period, setPeriod] = useState<BillingCycle>('current')
  const [customFromDate, setCustomFromDate] = useState<Date | undefined>()
  const [customToDate, setCustomToDate] = useState<Date | undefined>()
  const [customFromOpen, setCustomFromOpen] = useState(false)
  const [customToOpen, setCustomToOpen] = useState(false)

  const { fromDate, toDate } = useMemo(
    () => getPeriodDates(period, customFromDate, customToDate),
    [period, customFromDate, customToDate]
  )

  const periodLabel = getTimeRangeLabel(period)
  const queryKeyBase = ['customer-overview', customer.id, fromDate, toDate]

  // Independent queries — each loads/fails on its own
  const dailyUsageQuery = useQuery({
    queryKey: [...queryKeyBase, 'dailyUsage'],
    queryFn: () =>
      customerService.getCustomerDailyUsage(customer.id, fromDate, toDate),
  })

  const liveBillQuery = useQuery<LiveBillResponse>({
    queryKey: [...queryKeyBase, 'liveBill'],
    queryFn: () =>
      customerDetailService.getLiveBill(customer.id, fromDate, toDate),
  })

  const metricsQuery = useQuery<CustomerOverviewMetricsResponse>({
    queryKey: ['customer-overview', customer.id, 'metrics'],
    queryFn: () =>
      customerDetailService.getCustomerOverviewMetrics(customer.id),
  })

  const receiptsQuery = useQuery({
    queryKey: [...queryKeyBase, 'receipts'],
    queryFn: () =>
      customerDetailService.getValueReceiptsForCustomer(
        customer.id,
        fromDate,
        toDate
      ),
  })

  const agentUsageQuery = useQuery<CustomerAgentUsageResponse>({
    queryKey: [...queryKeyBase, 'agentUsage'],
    queryFn: () =>
      customerService.getCustomerAgentUsage(customer.id, fromDate, toDate),
  })

  const handleRefresh = () => {
    queryClient.invalidateQueries({
      queryKey: ['customer-overview', customer.id],
    })
  }

  // Derived values — gracefully fallback when individual queries haven't loaded
  const liveBillData = liveBillQuery.data ?? null
  const overviewMetrics = metricsQuery.data ?? null
  const agentUsageData = agentUsageQuery.data ?? null
  const totalValue = (receiptsQuery.data as Record<string, unknown>)?.summary
    ? (((
        (receiptsQuery.data as Record<string, unknown>).summary as Record<
          string,
          unknown
        >
      )?.total_value as number) ?? null)
    : null
  const usageData = dailyUsageQuery.data?.daily ?? []
  const agentTotalCost = (agentUsageData?.agents ?? []).reduce(
    (s, a) => s + a.total_cost,
    0
  )

  const agentCostMap = new Map(
    (agentUsageData?.agents ?? []).map(a => [a.agent_id, a.total_cost])
  )
  const totalTasks = (liveBillData?.agents ?? []).reduce(
    (s, a) => s + a.tasks,
    0
  )
  const totalWorkflows = (liveBillData?.agents ?? []).reduce(
    (s, a) => s + a.workflows,
    0
  )
  const totalOutcomes = (liveBillData?.agents ?? []).reduce(
    (s, a) => s + a.outcomes,
    0
  )
  const totalRevenue = liveBillData?.summary.total_revenue_usd ?? 0
  const totalCost = agentTotalCost
  const marginUsd = totalRevenue - totalCost
  const marginPct =
    totalRevenue > 0
      ? Math.round(((totalRevenue - totalCost) / totalRevenue) * 100)
      : null

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex justify-between items-center'>
        <h2 className='text-2xl font-bold'>Customer Overview</h2>
        <Button variant='outline' size='sm' onClick={handleRefresh}>
          <RefreshCw className='w-4 h-4' />
        </Button>
      </div>

      {/* Period Selector */}
      <div className='flex justify-start items-center gap-4'>
        <BillingCycleSelector
          value={period}
          onValueChange={setPeriod}
          className='w-48'
        />

        {/* Custom Date Range Selectors */}
        {period === 'custom_range' && (
          <div className='flex items-center gap-2 ml-4 pl-4 border-l'>
            <span className='text-sm text-muted-foreground'>From:</span>
            <Popover open={customFromOpen} onOpenChange={setCustomFromOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant='outline'
                  className='w-44 h-9 justify-between font-normal'
                >
                  {customFromDate
                    ? customFromDate.toLocaleDateString()
                    : 'Select date'}
                  <ChevronDown className='h-4 w-4' />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className='w-auto overflow-hidden p-0'
                align='start'
              >
                <CalendarComponent
                  mode='single'
                  selected={customFromDate}
                  captionLayout='dropdown'
                  onSelect={date => {
                    setCustomFromDate(date)
                    setCustomFromOpen(false)
                  }}
                />
              </PopoverContent>
            </Popover>
            <span className='text-muted-foreground text-sm'>to</span>
            <Popover open={customToOpen} onOpenChange={setCustomToOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant='outline'
                  className='w-44 h-9 justify-between font-normal'
                >
                  {customToDate
                    ? customToDate.toLocaleDateString()
                    : 'Select date'}
                  <ChevronDown className='h-4 w-4' />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className='w-auto overflow-hidden p-0'
                align='start'
              >
                <CalendarComponent
                  mode='single'
                  selected={customToDate}
                  captionLayout='dropdown'
                  onSelect={date => {
                    setCustomToDate(date)
                    setCustomToOpen(false)
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>

      {/* Key Metrics Cards */}
      <div className='space-y-3'>
        {/* First Row: Active Contracts, Tasks, Workflows, Outcomes */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3'>
          {metricsQuery.isLoading ? (
            <CardSkeleton />
          ) : (
            <Card className='bg-transparent border h-24 flex items-center'>
              <CardContent className='px-3 py-0 w-full'>
                <div className='flex items-center justify-between'>
                  <span className='text-md font-medium'>Active Contracts</span>
                  <span className='text-md font-bold'>
                    {overviewMetrics?.active_contracts ?? 0}
                  </span>
                </div>
                <p className='text-xs text-muted-foreground mt-1'>
                  Customer since{' '}
                  {overviewMetrics
                    ? formatDate(overviewMetrics.customer_since)
                    : 'N/A'}
                </p>
              </CardContent>
            </Card>
          )}

          {liveBillQuery.isLoading ? (
            <>
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
            </>
          ) : (
            <>
              <Card className='bg-transparent border h-24 flex items-center'>
                <CardContent className='px-3 py-0 w-full'>
                  <div className='flex items-center justify-between'>
                    <span className='text-md font-medium'>Tasks Completed</span>
                    <span className='text-md font-bold'>
                      {totalTasks.toLocaleString()}
                    </span>
                  </div>
                  <p className='text-xs text-muted-foreground mt-1'>
                    {periodLabel}
                  </p>
                </CardContent>
              </Card>

              <Card className='bg-transparent border h-24 flex items-center'>
                <CardContent className='px-3 py-0 w-full'>
                  <div className='flex items-center justify-between'>
                    <span className='text-md font-medium'>
                      Workflows Completed
                    </span>
                    <span className='text-md font-bold'>
                      {totalWorkflows.toLocaleString()}
                    </span>
                  </div>
                  <p className='text-xs text-muted-foreground mt-1'>
                    {periodLabel}
                  </p>
                </CardContent>
              </Card>

              <Card className='bg-transparent border h-24 flex items-center'>
                <CardContent className='px-3 py-0 w-full'>
                  <div className='flex items-center justify-between'>
                    <span className='text-md font-medium'>
                      Outcomes Completed
                    </span>
                    <span className='text-md font-bold'>
                      {totalOutcomes.toLocaleString()}
                    </span>
                  </div>
                  <p className='text-xs text-muted-foreground mt-1'>
                    {periodLabel}
                  </p>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Second Row: Revenue, Cost, Margin, Total Value */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3'>
          {liveBillQuery.isLoading ? (
            <>
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
            </>
          ) : (
            <>
              <Card className='bg-transparent border h-24 flex items-center'>
                <CardContent className='px-3 py-0 w-full'>
                  <div className='flex items-center justify-between'>
                    <span className='text-md font-medium'>Revenue</span>
                    <span className='text-md font-bold'>
                      {formatCurrency(totalRevenue)}
                    </span>
                  </div>
                  <p className='text-xs text-muted-foreground mt-1'>
                    {periodLabel}
                  </p>
                </CardContent>
              </Card>

              <Card className='bg-transparent border h-24 flex items-center'>
                <CardContent className='px-3 py-0 w-full'>
                  <div className='flex items-center justify-between'>
                    <span className='text-md font-medium'>Cost</span>
                    <span className='text-md font-bold'>
                      {formatCurrency(totalCost)}
                    </span>
                  </div>
                  <p className='text-xs text-muted-foreground mt-1'>
                    Operating costs
                  </p>
                </CardContent>
              </Card>

              <Card className='bg-transparent border h-24 flex items-center'>
                <CardContent className='px-3 py-0 w-full'>
                  <div className='flex items-center justify-between'>
                    <span className='text-md font-medium'>Profit Margin</span>
                    <span
                      className={`text-md font-bold ${marginUsd >= 0 ? '' : 'text-red-500'}`}
                    >
                      {formatCurrency(marginUsd)}
                    </span>
                  </div>
                  <div className='flex items-center space-x-1 text-xs text-muted-foreground mt-1'>
                    <span>
                      {marginPct !== null ? `${marginPct}%` : 'N/A'} margin
                    </span>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {receiptsQuery.isLoading ? (
            <CardSkeleton />
          ) : (
            <Card className='bg-transparent border h-24 flex items-center'>
              <CardContent className='px-3 py-0 w-full'>
                <div className='flex items-center justify-between'>
                  <span className='text-md font-medium'>Total Value</span>
                  <span className='text-md font-bold text-green-600'>
                    {totalValue !== null && totalValue > 0
                      ? formatCurrency(totalValue)
                      : '—'}
                  </span>
                </div>
                <p className='text-xs text-muted-foreground mt-1'>
                  Agent-delivered value
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      <div className='border-t mt-5 border-black' />

      {/* Agent Usage Details */}
      <div className='space-y-4'>
        <h3 className='text-lg font-semibold text-slate-600'>Agents</h3>

        {liveBillQuery.isLoading ? (
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
            {Array.from({ length: 3 }).map((_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
              <Card key={i} className='bg-transparent border'>
                <CardContent className='p-6'>
                  <div className='animate-pulse space-y-3'>
                    <div className='h-6 bg-slate-300 dark:bg-slate-600 rounded w-1/3' />
                    <div className='h-4 bg-slate-300 dark:bg-slate-600 rounded w-1/2' />
                    <div className='h-20 bg-slate-300 dark:bg-slate-600 rounded' />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
              {(liveBillData?.agents ?? []).map((agent: LiveBillAgent) => {
                const agentCost =
                  agentCostMap.get(agent.agent_id) ?? agent.cost_usd
                const agentMarginUsd = agent.revenue_usd - agentCost
                const agentMarginPct =
                  agent.revenue_usd > 0
                    ? Math.round((agentMarginUsd / agent.revenue_usd) * 100)
                    : null
                return (
                  <Card key={agent.agent_id} className='bg-transparent border'>
                    <CardHeader>
                      <div className='flex justify-between items-start'>
                        <div>
                          <CardTitle className='text-lg'>
                            {agent.agent_name}
                          </CardTitle>
                          <CardDescription>
                            Last activity:{' '}
                            {agent.last_activity
                              ? formatDate(agent.last_activity)
                              : 'No activity'}
                          </CardDescription>
                        </div>
                        <div className='text-right'>
                          <div
                            className={`text-xl font-bold ${agentMarginUsd >= 0 ? 'text-green-600' : 'text-red-500'}`}
                          >
                            {formatCurrency(agentMarginUsd)}
                          </div>
                          <div className='text-sm text-muted-foreground'>
                            Margin (
                            {agentMarginPct !== null
                              ? `${agentMarginPct}%`
                              : 'N/A'}
                            )
                          </div>
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent className='space-y-4'>
                      <div className='space-y-3'>
                        <div className='grid grid-cols-3 gap-2'>
                          <div className='text-center p-2 bg-muted/50 rounded-lg'>
                            <div className='text-lg font-bold text-slate-600'>
                              {agent.tasks.toLocaleString()}
                            </div>
                            <div className='text-xs text-muted-foreground'>
                              Tasks
                            </div>
                          </div>
                          <div className='text-center p-2 bg-muted/50 rounded-lg'>
                            <div className='text-lg font-bold text-slate-600'>
                              {agent.workflows.toLocaleString()}
                            </div>
                            <div className='text-xs text-muted-foreground'>
                              Workflows
                            </div>
                          </div>
                          <div className='text-center p-2 bg-muted/50 rounded-lg'>
                            <div className='text-lg font-bold text-slate-600'>
                              {agent.outcomes.toLocaleString()}
                            </div>
                            <div className='text-xs text-muted-foreground'>
                              Outcomes
                            </div>
                          </div>
                        </div>

                        <div className='grid grid-cols-2 gap-2'>
                          <div className='text-center p-2 bg-muted/50 rounded-lg'>
                            <div className='text-lg font-bold'>
                              {formatCurrency(agent.revenue_usd)}
                            </div>
                            <div className='text-xs text-muted-foreground'>
                              Revenue
                            </div>
                          </div>
                          <div className='text-center p-2 bg-muted/50 rounded-lg'>
                            <div className='text-lg font-bold'>
                              {formatCurrency(agentCost)}
                            </div>
                            <div className='text-xs text-muted-foreground'>
                              Cost
                            </div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h4 className='font-medium mb-2'>Top Signals</h4>
                        <div className='space-y-1'>
                          {agent.top_signals.slice(0, 2).map(sig => (
                            <div
                              key={sig.signal_id}
                              className='flex items-center justify-between p-2 border rounded text-sm'
                            >
                              <div className='flex items-center gap-2'>
                                <Zap className='w-4 h-4 text-blue-500' />
                                <span className='font-medium'>
                                  {sig.signal_name}
                                </span>
                              </div>
                              <span className='font-medium'>
                                {sig.quantity.toLocaleString()}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className='space-y-1'>
                        <div className='flex justify-between text-sm'>
                          <span>Cost Efficiency</span>
                          <span>{agentMarginPct}%</span>
                        </div>
                        <Progress value={agentMarginPct} className='h-1' />
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>

            {(liveBillData?.agents ?? []).length === 0 && (
              <Card className='bg-transparent border'>
                <CardContent className='text-center py-12'>
                  <Bot className='w-12 h-12 mx-auto mb-4 text-muted-foreground/50' />
                  <h3 className='text-lg font-medium mb-2'>
                    No usage data found
                  </h3>
                  <p className='text-sm text-muted-foreground max-w-sm mx-auto'>
                    This customer hasn't used any agents yet. Usage data will
                    appear here once they start using services.
                  </p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      <div className='border-t mt-5 border-black' />

      {/* Charts */}
      <div className='grid grid-cols-1 lg:grid-cols-1 gap-6'>
        <Card className='bg-transparent border'>
          <CardContent className='p-6'>
            {dailyUsageQuery.isLoading ? (
              <div className='animate-pulse'>
                <div className='h-6 bg-slate-300 dark:bg-slate-600 rounded w-1/3 mb-4' />
                <div className='h-64 bg-slate-300 dark:bg-slate-600 rounded' />
              </div>
            ) : (
              <RevenueChart
                data={usageData}
                title={`Financial Performance (${periodLabel})`}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
